const MS_PER_DAY = 86_400_000;

/**
 * Helpers for date-only ISO strings ("2026-09-07").
 * All math runs on UTC day numbers so local timezone never shifts a date.
 */

export function todayIso(): string {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export function addDays(iso: string, days: number): string {
  const date = new Date(toUtcMs(iso) + days * MS_PER_DAY);
  return toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

/** Whole days from `from` to `to`; positive when `to` is later. */
export function diffDays(from: string, to: string): number {
  return Math.round((toUtcMs(to) - toUtcMs(from)) / MS_PER_DAY);
}

/** 0 = Sunday ... 6 = Saturday */
export function dayOfWeek(iso: string): number {
  return new Date(toUtcMs(iso)).getUTCDay();
}

export function isWeekend(iso: string): boolean {
  const day = dayOfWeek(iso);
  return day === 0 || day === 6;
}

export function dayOfMonth(iso: string): number {
  return new Date(toUtcMs(iso)).getUTCDate();
}

/** "07.09" */
export function formatDayMonth(iso: string): string {
  const date = new Date(toUtcMs(iso));
  return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}`;
}

/** "07.09.2026" */
export function formatDayMonthYear(iso: string): string {
  const date = new Date(toUtcMs(iso));
  return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`;
}

/** "Wrzesień 2026" */
export function formatMonthYear(iso: string): string {
  const date = new Date(toUtcMs(iso));
  const month = MONTH_NAMES[date.getUTCMonth()];
  return `${month} ${date.getUTCFullYear()}`;
}

/**
 * "2026-10-01" → the UTC instant of that day's *local* midnight, ISO 8601 with Z. The one helper
 * here that reads the browser's zone on purpose: the Raporty range is sent to an API that knows
 * no time zone, so a calendar day's boundaries are the browser's, which is the plant's.
 */
export function localMidnight(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toISOString();
}

export const WEEKDAY_SHORT = ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'];

const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

function toUtcMs(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
